import React, { Component } from "react";
import "./Typeahead.scss";
import { get, isNil, debounce, slice, isEqual, toLower, differenceWith } from "lodash-es";
import ActivityIndicator from "../ActivityIndicator/ActivityIndicator";
import OwInputWrapper from "../OwInputWrapper/OwInputWrapper";
import OwLabel from "../OwLabel/OwLabel";
import OwButton from "../OwButton/OwButton";
import { Dropdown, DropdownButton, SplitButton } from "react-bootstrap";

/**
 * Typeahead Component
 *
 * This component allows you to get a very nice, and simple typeahead within the application.
 *
 *
 * titleProp        {String}
 * subTitleProp     {String}
 * selectedItemTitleProp {String}
 * maxResults       {Integer}
 * placeholder      {String}
 * selectedItemFn   {Function}
 * typeaheadApiFn   {Function}
 * disabled         {Boolean}
 */
type Props = {
    selectedItem?: any;
    /**
     * Function called after the typeahead is cleared so that the parent component flag can be changed to false.
     */
    clearTypeaheadCallback?: () => void;
    /**
     * An array of data items that should populate the selectable list.
     */
    typeaheadItems?: any[];
    /**
     * A string for the property in the typeaheadItems item to populate the title with.
     */
    titleProp?: string;
    /**
     * A string for the property in the typeaheadItems item to populate the subtitle with.
     */
    subTitleProp?: string;
    /**
     * Override the default 'name' title property
     */
    selectedItemTitleProp?: string;
    /**
     * The maximum number of search results to show.
     */
    maxResults?: number;
    /**
     * Display an '+' button that appears to be part of the typeahead, for a quick and dirty add item button. Calls selectedItemFn
     */
    confirmAdd?: boolean;
    /**
     * Show a side filter dropdown button
     */
    sideFilter?: boolean;
    /**
     * An array of items to show in the side filter dropdown
     */
    sideFilterItems?: { name: string; children: React.ReactNode }[];
    /**
     * Function to call when a side filter item is selected
     */
    sideFilterFn?: (item: any) => void;
    /**
     * Function that determines if the item returned from the API is selectable.
     */
    isSelectableFn: any;
    /**
     * Function to pass the typed value to, in order to make the proper API call.
     */
    typeaheadApiFn: (value: string) => void;
    /**
     * Function to pass the selected item to.
     */
    selectedItemFn?: any;
    /**
     * Optional text to display to the user if no results from lookup
     */
    notFoundText?: string;
    /**
     * Determines if the control should be disabled or not.
     */
    disabled?: boolean;
    /**
     * Flag to clear the typeahead and reset all values/flags
     */
    clearTypeahead?: boolean;
    /**
     * The placeholder for the input field
     */
    placeholder?: string;
    /**
     * The classname(s) added to the input component
     */
    className?: string;
    /**
     * The classname(s) to add to the div that directly wraps the input component
     */
    wrapperClassName?: string;
    /**
     * The classname(s) to add to the outer most div for the component
     */
    outerWrapperClassName?: string;
    /**
     * Perform specific rendering to the Typeahead Items and how they're displayed
     */
    itemsRenderFn?: (item: any) => any;
    /**
     * Whether or not the Input should be marked required
     */
    isRequired?: boolean;
    /**
     * The title value for the Input component
     */
    title?: string;
    /**
     * Function called on a keyDown event in the input
     *
     * @param e
     */
    onKeyDownFn?: (e: any) => void;
    /**
     * If we should show a 'Create New' as the first option to the User.
     */
    showCreateNew?: boolean;
    /**
     * What function we should call if the user selects 'Create New'
     */
    createNewFn?: (value?: string) => void;
    /**
     * Enable the results list to be scrollable, with a max height of 4-5 items
     */
    scrollableList?: boolean;
    /**
     * Specify the class override for the form-control
     */
    defaultClassName?: string;
    /**
     * Auto populate the typeahead with a search phrase.
     */
    searchPhrase?: string;
    /**
     * A test id for the typeahead component input field
     */
    inputTestId?: string;
    /**
     * Override the Create New label text
     */
    createNewText?: string;
    /**
     * Stick the create new button to the bottom of the list, and always visible
     */
    stickyCreateNewToBottom?: boolean;
    /**
     * Stick the create new button to the top of the list, and always visible
     */
    stickyCreateNewToTop?: boolean;

    /**
     * If true, the typeahead will be auto focused
     */
    autoFocus?: boolean;
};

type State = {
    typeahead: string;
    clearTypeahead: boolean;
    clearTypeaheadCallback: any;
    selectedItem: any;
    typeaheadItems: any;
    titleProp: string;
    subTitleProp: string;
    selectedItemTitleProp: string;
    maxResults: number;
    confirmAdd: boolean;
    isSelectableFn: any;
    selectedItemFn: any;
    notFoundText: string;
    hasTypeaheadText: boolean;
    hasTypeaheadItems: boolean;
    hasSelectedItem: boolean;
    isInputDirty: boolean;
    performingSearch: boolean;
    indicatorBarInterval?: any;
    indicatorBarPercentage: number;
    showSideItems: boolean;
};

class Typeahead extends Component<Props, State> {
    enableVisualDebouce = false;

    readonly state: State = {
        typeahead: get(this.props, "selectedItem.name", ""),
        clearTypeahead: false,
        clearTypeaheadCallback: get(this.props, "clearTypeaheadCallback", undefined),
        selectedItem: get(this.props, "selectedItem", null),
        typeaheadItems: get(this.props, "typeaheadItems", null),
        titleProp: get(this.props, "titleProp", ""),
        subTitleProp: get(this.props, "subTitleProp", ""),
        selectedItemTitleProp: this.props.selectedItemTitleProp || "name",
        maxResults: get(this.props, "maxResults", 10),
        confirmAdd: get(this.props, "confirmAdd", false),
        isSelectableFn: this.props.isSelectableFn,
        selectedItemFn: this.props.selectedItemFn,
        notFoundText: get(this.props, "notFoundText", "Nothing found."),
        hasTypeaheadText: false,
        hasTypeaheadItems: false,
        hasSelectedItem: false,
        isInputDirty: false,
        performingSearch: false,
        indicatorBarPercentage: 100,
        showSideItems: false,
    };

    constructor(props: any) {
        super(props);
    }

    async componentDidMount() {
        // console.log("typeahead: ", this.props.selectedItem, this.props.selectedItemTitleProp, this.state.selectedItemTitleProp);
        // this.props.socket.on('onTypeaheadCatalogItem', this.onTypeaheadCatalogItem);
        if (!isNil(this.props.selectedItem)) {
            // console.log("inital selected typeahead value: ", get(this.props, `selectedItem.${this.state.selectedItemTitleProp}`, ""));
            this.setState({
                typeahead: get(this.props, `selectedItem.${this.state.selectedItemTitleProp}`, ""),
            });
        }
    }

    async componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.clearTypeahead) {
            this.clearTypeahead();
        }

        if (isNil(prevProps.selectedItem)) {
            if (!isNil(this.props.selectedItem)) {
                // console.log("inital selected typeahead value: ", get(this.props, `selectedItem.${this.state.selectedItemTitleProp}`, ""));
                this.setState({
                    typeahead: get(this.props, `selectedItem.${this.state.selectedItemTitleProp}`, ""),
                });
            }
        }

        if (prevState.performingSearch) {
            this.setState({
                performingSearch: false,
            });
        }

        if (prevProps.searchPhrase !== this.props.searchPhrase && this.props.searchPhrase) {
            this.setState(
                {
                    typeahead: this.props.searchPhrase,
                },
                () => {
                    if (this.state.typeahead.length) {
                        this.typeaheadApiFn();
                    }
                }
            );
        }

        // this.setState({
        //     // typeaheadItems: this.props.typeaheadItems,
        // })
    }

    async componentWillUnmount() {
        // this.props.socket.removeListener('onTypeaheadCatalogItem', this.onTypeaheadCatalogItem);
    }

    clearTypeahead = () => {
        if (this.props.disabled) {
            return;
        }

        this.setState({
            indicatorBarPercentage: 100,
            typeahead: "",
            isInputDirty: false,
            selectedItem: null,
        });

        if (this.state.clearTypeaheadCallback) {
            this.state.clearTypeaheadCallback();
        }
    };

    /**
     * Function called when the user types into the Typeahead.
     *
     * @property event {object}    The type event
     */
    typeaheadApiFn = (event?: React.KeyboardEvent<HTMLInputElement>) => {
        // let value = event.currentTarget.value;
        // console.log('Typeahead FN Call: ', Date.now());

        const state: any = {
            // typeahead: value,
            performingSearch: true,
            hasTypeaheadText: true,
            isInputDirty: true,
            selectedItem: null,
            hasSelectedItem: false,
        };

        if (!this.state.typeahead.length) {
            state["typeaheadItems"] = null;
            state["hasTypeaheadText"] = false;
        }

        this.props.typeaheadApiFn(this.state.typeahead);

        this.setState(state);
    };

    setIndicatorWidth = () => {
        const { indicatorBarPercentage, indicatorBarInterval } = this.state;

        let newBarPercentage = 0;

        if (indicatorBarPercentage > 10) {
            newBarPercentage = indicatorBarPercentage - 10;
        } else {
            clearInterval(indicatorBarInterval);
        }

        this.setState({
            indicatorBarPercentage: newBarPercentage,
        });
    };

    debouncedTypeaheadApiFn = debounce(this.typeaheadApiFn, 800);

    persistTypeaheadApiFn = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape" || e.keyCode === 27) {
            this.setState(
                {
                    typeaheadItems: null,
                    isInputDirty: false,
                },
                () => {
                    console.log("typeaheadItems set to null.");
                }
            );

            return;
        }

        const value = e.currentTarget.value;
        const indicatorBarInterval = this.state.indicatorBarInterval;

        if (!isNil(indicatorBarInterval) && this.enableVisualDebouce) {
            clearInterval(indicatorBarInterval);
        }

        e.persist();

        const isInputDirty = !!value;

        const state: any = {
            indicatorBarInterval: this.enableVisualDebouce ? setInterval(this.setIndicatorWidth, 50) : null,
            indicatorBarPercentage: 100,
            typeahead: value,
            isInputDirty,
        };

        this.setState(state, () => {
            // console.log('Typeahead Debounced Call: ', Date.now());
            this.debouncedTypeaheadApiFn(e);
        });
    };

    selectedItemFn = (item: any) => {
        // console.log('selectedItemFn', item);
        if (!this.state.confirmAdd) {
            this.state.selectedItemFn(item);
        }

        clearInterval(this.state.indicatorBarInterval);

        const typeahead = get(item, `${this.state.titleProp}`, "");

        // if (this.state.subTitleProp) {
        //     const subTitleValue = get(item, `${this.state.subTitleProp}`, "") || "";
        //
        //     if (subTitleValue.length) {
        //         typeahead = typeahead + " - " + subTitleValue;
        //     }
        // }

        this.setState(
            {
                indicatorBarInterval: undefined,
                typeaheadItems: [],
                selectedItem: item,
                typeahead,
                hasSelectedItem: true,
                isInputDirty: false,
            },
            () => {
                // console.log("isInputDirty: " + this.state.isInputDirty);
            }
        );
    };

    addSelectedItemBtn = () => {
        if (!isNil(this.state.selectedItem)) {
            const item = this.state.selectedItem;
            // console.log("addSelectedItemBtn");

            this.setState(
                {
                    typeahead: "",
                    isInputDirty: false,
                    selectedItem: null,
                    hasSelectedItem: false,
                },
                () => {
                    this.state.selectedItemFn(item);
                }
            );
        }
    };

    createNewFn = () => {
        if (this.props.createNewFn) {
            this.props.createNewFn(this.state.typeahead);

            this.setState(
                {
                    indicatorBarInterval: undefined,
                    typeaheadItems: [],
                    // selectedItem: item,
                    // typeahead,
                    hasSelectedItem: true,
                    isInputDirty: false,
                },
                () => {
                    // console.log("isInputDirty: " + this.state.isInputDirty);
                }
            );
        } else {
            console.log("Missing Create New Fn");
        }
    };

    getTypeaheadItemsFormatted = (_typeaheadItems: any[], hideTypeahead: boolean) => {
        if (!isNil(_typeaheadItems)) {
            let typeaheadItemsFormatted;

            if (_typeaheadItems.length) {
                let typeaheadItems = _typeaheadItems;

                const maxResults = get(this.props, "maxResults", 10);

                typeaheadItems = slice(typeaheadItems, 0, maxResults - 1);

                if (_typeaheadItems.length > maxResults) {
                    typeaheadItems.push({
                        name: `More than ${maxResults} results. Please refine.`,
                        description: "",
                    });
                }

                typeaheadItemsFormatted = typeaheadItems.map((item, index) => {
                    const isSelectable = this.state.isSelectableFn(item);

                    // console.log(isSelectable);

                    let out_of_stock_message: any;
                    const hasInventory = item.has_inventory;

                    if (!isSelectable && hasInventory) {
                        out_of_stock_message = <OwLabel className={"text-warning ms-2 p-0"} title={"out_of_stock"} />;
                    } else {
                        if (!isNil(item.active) && !item.active && item.catalog_id) {
                            out_of_stock_message = <OwLabel className={"text-warning ms-2"} title={"inactive"} />;
                        }
                    }

                    let message: any = "";

                    if (!isNil(item)) {
                        if (!isNil(item.allow_backorder) && isSelectable && hasInventory) {
                            if (item.allow_backorder && !hasInventory) {
                                message = <OwLabel className={"text-warning ms-2"} title={"backorder"} noBottomMargin={true} />;
                            }
                        }

                        if (!isNil(item.is_product_group)) {
                            if (item.is_product_group) {
                                message = (
                                    <span className={"flex-grow text-end"}>
                                        <i title={"Product Group"} className={"far fa-sitemap text-primary"}></i>
                                    </span>
                                );
                            }
                        }
                    }

                    const title = get(item, `${this.state.titleProp}`);
                    const subTitle = get(item, `${this.state.subTitleProp}`);

                    return (
                        <div
                            role={"option"}
                            data-testid={`typeahead-item-${index}`}
                            className={
                                "typeahead-item d-flex w-100 p-2 " +
                                (!isNil(out_of_stock_message) && out_of_stock_message ? "disabled-pointer" : "pointer")
                            }
                            tabIndex={0}
                            key={index}
                            onKeyUp={(e: any) => {
                                if (e.key === "Enter") {
                                    if (!isNil(out_of_stock_message)) {
                                        return;
                                    }
                                    this.selectedItemFn(item);
                                }
                            }}
                            onClick={(e: any) => {
                                if (!isNil(out_of_stock_message)) {
                                    return;
                                }

                                this.selectedItemFn(item);
                            }}>
                            <span className="w-100">
                                {this.props.itemsRenderFn ? (
                                    this.props.itemsRenderFn(item)
                                ) : (
                                    <React.Fragment>
                                        <div className={"d-flex"}>
                                            {title}
                                            {out_of_stock_message || message}
                                        </div>
                                        <small className="text-muted">{subTitle}</small>
                                    </React.Fragment>
                                )}
                            </span>
                            {/* <a ref={`typeahead-item-${index}`}
                                    data-tip={item.description}
                                    onMouseEnter={() => { ReactTooltip.show(this.refs[`typeahead-item-${index}`]); }}
                                    onMouseLeave={() => { ReactTooltip.hide(); }}
                                    >
                                    <i className="fas fa-question-circle"></i>
                                </a> */}
                        </div>
                    );
                });
            } else {
                typeaheadItemsFormatted = <div className="typeahead-item d-flex w-100 p-2">{this.state.notFoundText}</div>;
            }

            if (this.props.showCreateNew && this.props.createNewFn) {
                if (!this.props.stickyCreateNewToBottom && !this.props.stickyCreateNewToTop) {
                    typeaheadItemsFormatted = (
                        <React.Fragment>
                            {typeaheadItemsFormatted}
                            <div className={"text-center create-new"}>
                                <OwLabel
                                    title={this.props.createNewText || "create_new"}
                                    leftIconClass={"fas fa-plus"}
                                    fn={this.createNewFn}
                                    className={"pointer text-small"}
                                />
                            </div>
                        </React.Fragment>
                    );
                }
            }

            if (this.props.stickyCreateNewToBottom || this.props.stickyCreateNewToTop) {
                const createNewElem = (
                    <div className={"text-center create-new"}>
                        <OwLabel
                            title={this.props.createNewText || "create_new"}
                            leftIconClass={"fas fa-plus"}
                            fn={this.createNewFn}
                            className={"pointer text-small"}
                        />
                    </div>
                );

                return (
                    <div
                        className={
                            (this.props.scrollableList ? "scrollable-wrapper" : "") +
                            " position-absolute " +
                            (hideTypeahead ? "d-none" : "d-block w-100")
                        }>
                        {this.props.stickyCreateNewToTop ? createNewElem : null}
                        <div className={(this.props.scrollableList ? "scrollable-typeahead" : "") + " typeahead-items "}>
                            {typeaheadItemsFormatted}
                        </div>
                        {this.props.stickyCreateNewToBottom ? createNewElem : null}
                    </div>
                );
            } else {
                return (
                    <div
                        className={
                            (this.props.scrollableList ? "scrollable-typeahead" : "") +
                            " typeahead-items position-absolute " +
                            (hideTypeahead ? "d-none" : "d-block w-100")
                        }>
                        {typeaheadItemsFormatted}
                    </div>
                );
            }
        }
    };

    isArrayEqual = (x: any, y: any) => {
        return differenceWith(x, y, isEqual);
    };

    getAddButton = () => {
        const selectedItemClasses = "bg-primary text-white pointer";

        return <OwButton leftIconClass={"fas fa-plus"} title={"add"} fn={this.addSelectedItemBtn} disabled={!this.props.selectedItem} />;

        // return (
        //     <div className={"input-group-append "}>
        //         <div
        //             className={
        //                 "input-group-text " +
        //                 (get(this.props, "disabled", false) ? "disabled " : "") +
        //                 (this.state.selectedItem ? selectedItemClasses : "no-selected-item")
        //             }
        //             onClick={this.addSelectedItemBtn}>
        //             <OwLabel leftIconClass={"fas fa-plus"} title={"add"} noBottomMargin={true} />
        //         </div>
        //     </div>
        // );
    };

    toggleSideItems = () => {
        this.setState({
            showSideItems: !this.state.showSideItems,
        });
    };

    render() {
        // First Display Check
        const hasTypeaheadText = this.state.hasTypeaheadText;

        let typeaheadItems = this.state.typeaheadItems;

        if (this.isArrayEqual(typeaheadItems, this.props.typeaheadItems)) {
            typeaheadItems = this.props.typeaheadItems;
        }

        if (!hasTypeaheadText) {
            typeaheadItems = undefined;
        }

        const hasSelectedItem = !isNil(this.state.selectedItem);

        if (this.state.confirmAdd && hasSelectedItem && !this.state.isInputDirty) {
            typeaheadItems = undefined;
        }

        // Remaining Display Checks
        const hasTypeaheadItems = !isNil(typeaheadItems);

        let hideTypeahead = true;

        if (this.state.isInputDirty && hasTypeaheadItems) {
            hideTypeahead = false;
        }

        const typeaheadItemsFormatted = this.getTypeaheadItemsFormatted(typeaheadItems, hideTypeahead);
        // console.log('typeaheadItems Render: ', typeaheadItemsFormatted)

        const addButtonDetail = this.getAddButton();

        let isDisabled = false;

        if (get(this.props, "disabled", false)) {
            isDisabled = true;
        }

        const widthPerc = this.state.indicatorBarPercentage;

        let height = "3px";

        if (!this.enableVisualDebouce) {
            height = "0px";
        }

        return (
            <div className={"Typeahead-outer position-relative " + this.props.outerWrapperClassName}>
                {this.enableVisualDebouce ? (
                    <div className={"faded-orange-bg position-absolute "} style={{ width: `${widthPerc}%`, height: `${height}` }}></div>
                ) : null}
                <div className={`w-100 Typeahead ${this.props.wrapperClassName || ""}`}>
                    <div className={this.props.confirmAdd || this.props.sideFilter ? "input-group" : ""}>
                        {this.props.sideFilterItems ? (
                            <div>
                                <div
                                    className={
                                        "btn btn-secondary position-relative rounded-end-0 d-flex justify-content-center align-items-center h-100"
                                    }
                                    onClick={this.toggleSideItems}>
                                    <i className={"fas fa-filter"} />
                                    <i className={"fas fa-sort-down ms-2"} />
                                </div>
                                {this.state.showSideItems ? (
                                    <div className={"sideFilterItemsContainer position-absolute"}>
                                        {this.props.sideFilterItems?.map((item, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    onClick={() => (this.props.sideFilterFn ? this.props.sideFilterFn(item) : null)}>
                                                    {item.children}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        <OwInputWrapper
                            onKeyDownFn={this.props.onKeyDownFn}
                            title={this.props.title}
                            className={this.props.className + " "}
                            name={"typeahead_item"}
                            value={this.state.typeahead}
                            fn={this.persistTypeaheadApiFn}
                            placeholder={toLower(this.props.placeholder)}
                            disabled={isDisabled}
                            autoComplete={false}
                            isRequired={this.props.isRequired || false}
                            defaultClassName={this.props.defaultClassName}
                            testId={this.props.inputTestId}
                            autoFocus={this.props.autoFocus}
                        />
                        {this.state.performingSearch ? (
                            <span className={"search-activity position-absolute"}>
                                <i className={"fas fa-sync fa-spin"} />
                            </span>
                        ) : null}
                        <span
                            className={
                                (this.props.confirmAdd ? "typeahead-input-group-clear-shift" : "") +
                                " clear-icon position-absolute pointer " +
                                (!isNil(this.state.selectedItem) || this.state.typeahead.length ? "" : "d-none")
                            }
                            onClick={this.clearTypeahead}>
                            <i className={"fas fa-times"} />
                        </span>

                        {this.props.confirmAdd ? (
                            <OwButton
                                leftIconClass={"fas fa-plus"}
                                title={"add"}
                                fn={this.addSelectedItemBtn}
                                disabled={!this.state.selectedItem}
                            />
                        ) : null}
                    </div>

                    {/*<input autoComplete="off" className={"form-control " + this.props.className } type="text" name="typeahead_item" value={this.state.typeahead} onChange={this.typeaheadApiFn} placeholder={this.props.placeholder} disabled={get(this.props, 'disabled', false)}/>*/}

                    {typeaheadItemsFormatted}
                </div>
            </div>
        );
    }
}

export default Typeahead;
